import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { useJSONState, useLogout, useTranslate } from '../hooks';
import "../assets/style/Shipments.css"
import { TextField, Button, ConfirmDialog, Grid, Container, Group, Centralizer, Switch, Select, DataTable, Form, Popover } from '../components';
import { ShipmentType } from '../types/Shipments';
import { ConfirmDialogProps } from '../components/Utils/ConfirmDialog';
import Shipment from './Shipment';
import { format, set } from 'date-fns';
import { toast } from 'sonner';
import { deepCopyOf, dialog, sleep, Dialog } from '../utils';
import { FaCircleInfo, FaArrowRight } from "react-icons/fa6";
import DeliveryTypes from './DeliveryTypes';
import { CarrierType, DeliveryTypeType } from '../types';
import { axiosPrivate } from '../api/axios';

type statusSearch = {
  [key: string]: string
}

export default function Shipments() {
  // Hooks
  const api = useAxiosPrivate();
  const logout = useLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const [ statuses, setStatuses ] = useState<statusSearch[]>([])
  const [ triggerReload, setTriggerReload ] = useState(false);
  const [ inEOD, setInEOD ] = useState<boolean>(false);
  const [ showSearchTips, setShowSearchTips ] = useState<boolean>(false);
  const [ eod_All_Unprinted, set_eod_All_Unprinted] = useState<boolean>(true);
  const [ eod_Labels, set_eod_Labels] = useState(false);
  const [ eod_Waybills, set_eod_Waybills] = useState(false);
  const [ eod_Book, set_eod_Book] = useState(false);
  const [ eod_ShipmentList, set_eod_ShipmentList] = useState(false);  
  const [ eod_ShipmentsCount, set_eod_ShipmentsCount] = useState(0);
  const [ eod_maxShipmentId, set_eod_MaxShipmentId] = useState<string>(); 
  const [ deliveryTypes, setDeliveryTypes ] = useState<Pick<DeliveryTypeType, "id" | "name">[]>([])
  const [ carriers, setCarriers ] = useState<Pick<CarrierType, "name" | "carrierId">[]>([])
  const PAGE_SIZE = 50;

  // States
  const [shipments, setProp, setShipments] = useJSONState<ShipmentType[]>();
  const [newShipmentForm, setNewShipmentForm] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps>();
  const [showInterval, setShowInterval] = useState(false);
  const [searchFields, setSearchField, setSearchFields] = useJSONState({
    trackingNo: undefined,
    orderNumber: undefined,
    receiver: undefined,
    dispDate: undefined,
    carrier: undefined,
    deliveryType: undefined,
    status: undefined,
    shipmentId: undefined,
    dispDateFrom: undefined,
    dispDateTo: undefined,
  })

  // Defaults
  const dialogDefault = {
    title: t("NoShipmentFound"),
    message: t("VerifySearchConditions"),
    noCancel: true,
    type: "info",
    onOk: () => { setConfirmDialog(undefined) }
  } as ConfirmDialogProps

  const searchShipment = async (e: any, initial:boolean=false) => {
    if(e) e.preventDefault()
    let searchData = {} as any;

    if(initial){
      let sStorage = sessionStorage.getItem("searchFields");
      searchData = JSON.parse(sStorage ?? "{}")
      if(Object.keys(searchData).length == 0) return
      setSearchFields({...searchData})
    } 
    else sessionStorage.setItem("searchFields", JSON.stringify(searchFields ?? {}))

    setTriggerReload((v) => !v)
  }

  const fetchShipments = async ({page, snapshot, pageSize}: {
    page:number,
    snapshot: string,
    pageSize: number,
  }) => {
    try{
      const preDate = (new Date()).getTime()
      const response = await api.post({
        endpoint: "/ShipmentsList",
        data: {searchFields, page, snapshot, pageSize},
        mount: null
      })

      // For the search we want an indication of search. But not for page switch
      if(snapshot === ""){
        const afterDate = (new Date()).getTime()
        if(afterDate - preDate < 400) await sleep(400 - (afterDate - preDate))
      }          

      let data = response?.data?.data

      if(!data){
        //toast.info(t("NoShipmentFound") + ".\n" + t("VerifySearchConditions"))
        set_eod_ShipmentsCount(0);
        set_eod_MaxShipmentId("")
        setShipments([])
        return Promise.resolve()
      }

      const pages = response.data.pages
      snapshot = response.data.snapshot
      set_eod_ShipmentsCount(response.data.shipmentsCount);
      set_eod_MaxShipmentId(response.data.maxShipmentId)

      data = data.reduce((prev:any, cur:ShipmentType) => {
        const rec = cur.addresses.receiver
        const foo = {
          "CarrierSuite": (cur.carrierSuiteId != "STANDARD" ? 
              <img src={`/images/carriers/${cur.carrierSuiteId}.png`} alt="Carrier logotype"/> : 
              <div style={{fontFamily:"Monospace",fontSize:"1.1rem", fontWeight:600,letterSpacing:1.2}}>{cur.carrier}</div>
            ),
          orderNumber: cur.orderNumber,
          receiver: `${rec.name}\n${rec.street1}\n${rec.countryCode}-${rec.zipCode} ${rec.city}`,
          dispDate: cur.dispDate,
          deliveryType: cur.deliveryTypeName,
          status: <div className={getStatusElementClass(cur.status)}><p className='m-auto'>{t(`ta_statuses.${cur.status}`)}</p></div>,
          trackingNumber: cur.trackingLink ? <a className="tracking-number-link" title={t("track")} href={cur.trackingLink} target='_blank'>{cur.trackingNumber ?? t("track")}</a>:
              cur.trackingNumber ? <span>{cur.trackingNumber}</span> : "",
          noteInternal: cur.noteInternal !== '' ? <FaCircleInfo/> : null,
          shipmentId: cur.shipmentId
        }
        prev.push(foo)
        return prev
      }, [])

      return Promise.resolve({data:data as ShipmentType[], snapshot, pages})
    }
    catch(e){
      if(e?.response?.status !== 401) console.error(e)
      return Promise.resolve({data:[] as ShipmentType[], snapshot, pages: 0})
    }
  }


  const endOfDay = async () => {
    let title = t("eod.confirm").replace("{1}", eod_ShipmentsCount.toString())

    let options = {} as any
    const documents:("unPrinted" | "waybill" | "label" | "shipmentList" | "book")[] = [];

    if(eod_All_Unprinted){
      options["1"] = {type: "info", name: "- " + t("eod.unPrinted")}
      documents.push("unPrinted")
    }else{
      if(eod_Book){
        documents.push("book")
        options["1"] = {type: "info", name: "- " + t("eod.book")}
      } 
      if(eod_Waybills){
        documents.push("waybill")
        options["2"] = {type: "info", name: "- " + t("eod.waybills")}
      } 
      if(eod_Labels){
        documents.push("label")
        options["3"] = {type: "info", name: "- " + t("eod.labels")}
      }
    }  
    
    if(eod_ShipmentList){
      documents.push("shipmentList")
      options["4"] = {type: "info", name: "- " + t("eod.shipmentList")}
    }    

    const res = await dialog.open(options, {title}) 

    if(!res) return Promise.resolve()

    try{
      await api.post({
        endpoint: "shipments/print",
        mount: {
          loadingText: t("printingEOD"),
          onLoadText: t("sentToPrintClient")
        },
        data: {maxShipmentId: eod_maxShipmentId, searchFields, documents}
      })
    }catch(e){
      console.error(e)
    }  
  }

  useEffect(() => {
    setStatuses([
      {value:"10", name: t("open")},
      {value:"20", name: t("inProgress")},
      {value:"[12]0", name: t("openAndInProgress")},
      {value:"80", name: t("finished")},
      {value:"90", name: t("cancelled")},
    ])

    if ((location?.state as any)?.dialog !== undefined) {
      setConfirmDialog({ ...dialogDefault, ...(location?.state as any)?.dialog })
      window.history.replaceState({}, document.title)
    }

    api.get({
      endpoint: "/deliveryTypes",
      mount: null,
      params: {
        pageSize: 999,
        page: 1
      }
    }).then(res => setDeliveryTypes(res.data.data))
    api.get({
      endpoint: "/carriers",
      mount: null
    }).then(res => setCarriers(res.data))

    setTimeout(() => {
      console.log({deliveryTypes, carriers})
    }, 500);

    searchShipment(null, true)
  }, [])

  const props = (key: string) => {
    return {
      value: searchFields[key],
      onChange: (e: any) => setSearchField(e.target.value != "" ? e.target.value : undefined, key),
      onBlur: (e: any) => {
        if (e.target.value.trim().length === 0)
          setSearchField(undefined, key)

        if (key.toLocaleLowerCase().indexOf("date") > 0) {
          if (key === "dispDate") {
            let obj = { ...searchFields }
            delete obj.dispDateFrom
            delete obj.dispDateTo
            setSearchFields(obj)
          } else setSearchField(undefined, "dispDate")
        }
      } //if empty space, set undefined
    }
  }

  const getStatusElementClass = (status: number) => {
    let c = 'bg-gray-200 text-gray-600'
    if(status === 10) c = 'bg-gray-200 text-gray-600'
    else if (status === 20) c = 'bg-yellow-200 text-yellow-600'
    else if (status === 50) c = 'bg-blue-200 text-blue-600'
    else if (status === 70) c = 'bg-gray-400 text-gray-700'    
    else if (status === 80) c = 'bg-green-200 text-green-600'
    else if (status === 90) c = 'bg-orange-200 text-orange-600'
    else if (status === -99) c = 'bg-red-300 text-red-600'

    return `status flex content-center py-1 px-2 rounded font-medium text-center ${c}`    
  }

  return (
    <>
      {!newShipmentForm ? <>
        <Centralizer>
          <Container style={{ height: "70vh", maxHeight: "70vh", width: "clamp(0%, 100%, 1400px)" }}>
            <Group label={t("searchCriteria")}>
              <form onSubmit={(e) => searchShipment(e)}>
                <Grid>
                  <TextField {...props("trackingNo")}
                    title={t("enterConsOrPackageNo")}>{t("trackingNumber")}</TextField>
                  <TextField {...props("orderNumber")} id="shipment-orderno">{t("orderNumber")}</TextField>
                  <TextField {...props("receiver")}>{t("receiver")}</TextField>
                  <Select
                    onChange={(e) => setSearchField(e.target.value != "" ? e.target.value : undefined, "deliveryType")}
                    value={searchFields.deliveryType ?? ""}
                    data={deliveryTypes}
                    label={t("deliveryType")}
                    valueKey='name'
                    isDatalist={true}
                  />
                  {/* <TextField {...props("deliveryType")} id="shipment-deliveryType">{t("deliveryType")}</TextField> */}
                  {/* <TextField {...props("carrier")} id="shipment-carrier">{t("carrier")}</TextField> */}
                  <Select
                    onChange={(e) => setSearchField(e.target.value != "" ? e.target.value : undefined, "carrier")}
                    value={searchFields.carrier ?? ""}
                    data={carriers}
                    label={t("carrier")}
                    valueKey='name'
                    isDatalist={true}
                  />
                  <Select {...props("status")}
                    data={statuses as []}
                    label={t("status")} 
                  />
                  <TextField {...props("shipmentId")} id="shipment-shipmentId">{t("shipmentId")}</TextField>
                  <div className="w-full md:w-fit xl:w-full">
                    {!showInterval ?
                    <Grid>
                      <TextField {...props("dispDate")} _type="date">{t("dispDate")}</TextField>
                    </Grid>
                      :
                      <>
                        <Grid>
                          <TextField {...props("dispDateFrom")} _type="date">{t("dispDateFrom")}</TextField>
                          <TextField {...props("dispDateTo")} _type="date">{t("dispDateTo")}</TextField>
                        </Grid>
                      </>
                    }
                  </div> 
                </Grid>
         
                <Switch size='mini' onChange={() => setShowInterval(!showInterval)} checked={showInterval} label={t("showInterval")} />
                
                <Grid>
                  <Button className='w-full md:w-auto'>{t("search")}</Button>
                  <Button _type='button' className='w-full md:w-auto' onClick={() => { setSearchFields({}) }}>{t("clearsearch")}</Button>
                  <Button onClick={() => setShowSearchTips(true)} _type='button' variant='default'>{t("shipmentsSearch.link")+"..."}</Button>
                </Grid>                
              </form>       
            </Group>            
            <div className='w-full flex justify-end'>
              <Button onClick={() => setInEOD(true)} variant="default" disabled={!eod_maxShipmentId}>{t("endOfDay")}</Button>
              <Button onClick={() => setNewShipmentForm(true)} variant="add" id="CreateShipment">{t("createNewShipment")}</Button>              
            </div>
            <>
              {shipments && shipments.length > 0 && false &&
                <div className="schipt-table-body">
                  <table>
                    <thead>
                      <tr>
                        {/* <th></th> */}
                        <th><input type="checkbox" style={{width:"1.5em", height:"1.5em"}}
                          onChange={(e) => {
                            let checked = e.currentTarget.checked
                            let newShipments:ShipmentType[] = deepCopyOf(shipments)
                            newShipments.map(s => s.checked = checked)
                            setShipments(newShipments)
                          }}
                        /></th>
                        <th>{t("orderNumber")}</th>
                        <th>{t("receiver")}</th>
                        <th>{t("dispDate")}</th>
                        <th>{t("carrier")}</th>
                        <th>{t("status")}</th>
                        <th></th>
                        <th>{t("noteInternal")}</th>
                        <th>{t("shipmentId")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipments.map((s, i) => {
                        return (
                          <tr key={i} onDoubleClick={() => navigate(`/Shipment/${shipments[i].shipmentId}`)}>
                            {/* <td onClick={() => navigate(`/Shipment/${shipments[i].shipmentId}`)}><FaMagnifyingGlass className='text-schipt-black dark:text-schipt-white' /></td> */}
                            <td><input type="checkbox" checked={s.checked ?? false} style={{width:"2em", height:"2em"}}
                              onChange={() => setProp(!s.checked, i, "checked")}
                            /></td>
                            <td>{s.orderNumber}</td>                            
                            <td>
                              <div>{s.addresses.receiver.name}</div>
                              <div>{s.addresses.receiver.street1}</div>
                              <div>{s.addresses.receiver.countryCode + '-' + s.addresses.receiver.zipCode + ' ' + s.addresses.receiver.city}</div>
                            </td>
                            <td>{s.dispDate ? format(new Date(s?.dispDate), 'yyyy-MM-dd') : ""}</td>
                            <td>{s.carrier}</td>
                            
                            <td className={`status flex content-center mt-4 ${s?.status == 10 ? 'bg-gray-200 text-gray-600' : s?.status == 20 ? 'bg-yellow-200 text-yellow-600' : s?.status == 50 ? 'bg-blue-200 text-blue-600' : s?.status == 70 ? 'bg-gray-400 text-gray-700' : s?.status == 80 ? 'bg-green-200 text-green-600' : s?.status == 90 ? 'bg-orange-200 text-orange-600' : 'bg-gray-200 text-gray-600'}`}>{t(`ta_statuses.${s?.status }`)}</td>                            
                            
                            <td>{s.trackingLink &&
                              <Button className="schipt-btn secondary" onClick={() => {
                                s.trackingLink && window.open(s.trackingLink, 'targetWindow',
                                  `scrollbars=yes,
                                    resizable=yes,
                                    width=500,
                                    left=500,
                                    height=500`)
                              }} _type="button" variant='submit'>{t("track")}</Button>
                            }
                            </td>
                            <td>{s.noteInternal}</td>
                            <td>{s.shipmentId}</td>
                          </tr>)
                      })
                      }
                    </tbody>
                  </table>
                </div>
              }
              <DataTable 
                fetchData={fetchShipments}
                pageSize={PAGE_SIZE}
                hideColumns={["shipmentId"]}
                style={{maxHeight:"30rem"}} 
                rerender={triggerReload}
                openCallback={async (i:number, s:ShipmentType) => {
                  console.log({s})
                  navigate(`/Shipment/${s.shipmentId}`)
                }}
              />
            </>
          </Container>
        </Centralizer>
      </>
        :
        <>
          <Button variant='delete' className='absolute z-10 schipt-btn secondary' 
            onClick={() => setNewShipmentForm(false)}>{t("goBack")}
          </Button>
          <Shipment />
        </>
      }
      { inEOD &&
        <Dialog >
          <Form onSubmit={async () => {
            setInEOD(false);
            endOfDay()
          }}>
            <h3>{t("eod.header")}</h3>
            <Switch label={t("eod.unPrinted")} 
              checked={eod_All_Unprinted}
              onChange={() => set_eod_All_Unprinted(v => !v)}
            />
            { eod_All_Unprinted ? <></> :
              <Group label={t("eod.header2")} 
              style={{borderWidth:1, borderLeft:0, borderRight:0, borderBottom:0, borderRadius:0}}
              >
                <Switch label={t("eod.book")} 
                  checked={eod_Book}
                  onChange={() => set_eod_Book(v => !v)}
                />
                <Switch label={t("eod.labels")} 
                  checked={eod_Labels}
                  onChange={() => set_eod_Labels(v => !v)}
                />
                <Switch label={t("eod.waybills")} 
                  checked={eod_Waybills}
                  onChange={() => set_eod_Waybills(v => !v)}
                />                
              </Group>
            }           
            {/* <h3>{t("eod.shipmentListHeader")}</h3>  */}
            <Group label={t("eod.shipmentListHeader")}
              style={{borderWidth:1, borderLeft:0, borderRight:0, borderRadius:0}}
            >
              <Grid className="m-0">              
                <Switch label={t("eod.shipmentList")} 
                  checked={eod_ShipmentList}
                  onChange={() => set_eod_ShipmentList(v => !v)}
                  className='mt-0'
                />
                <Popover options={t("whatIsShipmentList")}>
                  <FaCircleInfo />
                </Popover>              
              </Grid>     
            </Group>       


            <Container>
              <Button variant='add' _type='submit' >{t("continue")+"..."}</Button>
              <Button variant='undo' _type='button' onClick={() => setInEOD(false)}>{t("cancel")}</Button>
            </Container>
          </Form>
        </Dialog>
      }
      {
        showSearchTips &&
        <Dialog>
          <Container id='shipmentsSearchInfo'>
            <h2>{t("shipmentsSearch.mainInfo")}</h2>
            <p>{t("shipmentsSearch.example0")}</p>
            <table>
              <tbody>
                <tr><td>{t("shipmentsSearch.example1").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example1").split(" | ")[1]}</td></tr>
                <tr><td>{t("shipmentsSearch.example2").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example2").split(" | ")[1]}</td></tr>
                <tr><td>{t("shipmentsSearch.example3").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example3").split(" | ")[1]}</td></tr>
                <tr><td>{t("shipmentsSearch.example4").split(" | ")[0]}</td><td><FaArrowRight /></td><td>{t("shipmentsSearch.example4").split(" | ")[1]}</td></tr>
              </tbody>
            </table>
            <Button variant='default' _type='button' onClick={() => setShowSearchTips(false)} >{t("ok")}</Button>
          </Container>
        </Dialog>
      }
      
      <ConfirmDialog options={confirmDialog as ConfirmDialogProps} />
    </>
  );
}